import { useCallback, useState } from 'react';

/**
 * useLegendToggle — small hook that makes Recharts legends clickable.
 *
 * Usage with Bar/Line/Area:
 *   const { onLegendClick, isHidden } = useLegendToggle();
 *   <Legend onClick={onLegendClick} formatter={(value) => (
 *     <span style={{ opacity: isHidden(value) ? 0.4 : 1, cursor: 'pointer' }}>{value}</span>
 *   )} />
 *   <Line dataKey="electricity" hide={isHidden('electricity')} ... />
 *
 * Usage with Pie (donut):
 *   <Legend onClick={(entry) => onLegendClick({ value: entry.value, dataKey: entry.value })} />
 *   then filter the pie's data array by isHidden(entry.name)
 *
 * Notes
 * -----
 * - Recharts passes the legend entry to onClick. The `value` field is the
 *   series name shown in the legend (and is the dataKey for non-Pie charts
 *   unless an explicit `name` is set).
 * - For Pie/Donut series, toggle by data-row name and filter the data prop.
 * - Click toggles; double-click resets all (handy when a user has hidden
 *   most series and wants everything back).
 */
export default function useLegendToggle(initialHidden = []) {
  const [hidden, setHidden] = useState(new Set(initialHidden));

  const isHidden = useCallback(
    (key) => hidden.has(key),
    [hidden],
  );

  const onLegendClick = useCallback((entry) => {
    // Recharts passes either {value, dataKey, ...} or just a string in some cases.
    const key = (entry && (entry.dataKey || entry.value)) || entry;
    if (key == null) return;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const resetHidden = useCallback(() => setHidden(new Set()), []);

  /**
   * Legend `formatter` helper — returns a span with dimmed/cursor styling
   * so users see which entries are toggled off.
   */
  const legendFormatter = useCallback((value) => {
    const dim = hidden.has(value);
    return (
      <span
        style={{
          opacity: dim ? 0.4 : 1,
          textDecoration: dim ? 'line-through' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        title={dim ? 'Click to show' : 'Click to hide'}
      >
        {value}
      </span>
    );
  }, [hidden]);

  return { hidden, isHidden, onLegendClick, resetHidden, legendFormatter };
}
